import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { STATUS_META } from "@/lib/constants";
import type { TurnkeyStatus } from "@/types";

/* ------------------------------------------------------------------ badges */
export function StatusBadge({ status }: { status: TurnkeyStatus }) {
  const meta = STATUS_META[status];
  return <span className={`badge badge--${status}`}>{meta.label}</span>;
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "glass";
}) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function Tag({ children, plain }: { children: ReactNode; plain?: boolean }) {
  return <span className={`tag${plain ? " tag--plain" : ""}`}>{children}</span>;
}

/* ------------------------------------------------------------------- chips */
interface FilterChipProps {
  active?: boolean;
  count?: number;
  onClick: () => void;
  children: ReactNode;
}

export function FilterChip({ active, count, onClick, children }: FilterChipProps) {
  return (
    <motion.button
      type="button"
      className={`chip${active ? " is-active" : ""}`}
      aria-pressed={active}
      onClick={onClick}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.14 }}
    >
      {children}
      {count !== undefined ? <span className="chip__count">{count}</span> : null}
    </motion.button>
  );
}

interface AppliedChipProps {
  label: string;
  value: string;
  onRemove: () => void;
}

/** A filter the user has applied, with its own dismiss control. */
export function AppliedChip({ label, value, onRemove }: AppliedChipProps) {
  return (
    <motion.span
      className="chip chip--applied"
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.18 }}
    >
      {label}: {value}
      <button type="button" onClick={onRemove} aria-label={`Remove ${label} filter`}>
        <Icon name="close" />
      </button>
    </motion.span>
  );
}

export function ClearChip({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      type="button"
      className="chip chip--clear"
      onClick={onClick}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      whileTap={{ scale: 0.96 }}
    >
      Clear all
    </motion.button>
  );
}

/* ------------------------------------------------------------------ rating */
export function Rating({ value, reviews, sales }: { value: number; reviews?: number; sales?: number }) {
  const suffix =
    reviews !== undefined && sales !== undefined ? ` (${reviews} reviews · ${sales} sold)` : "";

  return (
    <span className="plan-rating">
      <Icon name="star" />
      {value.toFixed(1)}
      {suffix ? <span>{suffix}</span> : null}
    </span>
  );
}

/* ----------------------------------------------------------------- eyebrow */
export function Eyebrow({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return <span className={`eyebrow${muted ? " eyebrow--muted" : ""}`}>{children}</span>;
}
